import { EventIdType, logAmplitudeEvent } from 'amplitude/amplitude'
import LoadingDots from 'layout/widgets/LoadingDots'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'

type PropTypes = {
  videoUrl: string
  trackingSource?: string
  notifId?: string
  eventId?: EventIdType
}

type PlayerType = {
  ready(): Promise<void>
  destroy(): void
  on(method: string, fnc: Function): void
}

declare global {
  interface Window {
    Vimeo: {
      Player: { new (element, options?): PlayerType }
    }
  }
}

const useStyles = makeOpenSolarStyles((theme) => ({
  container: { position: 'relative', width: 'inherit', height: 'inherit' },
}))

const ResponsiveVimeoPlayer: React.FC<PropTypes> = (props) => {
  const classes = useStyles()
  const [vimeoScriptLoaded, setVimeoScriptLoaded] = useState<boolean>(() => !!window.Vimeo)
  const [isIframeReady, setIsIframeReady] = useState<boolean>(false)
  const playerRef = useRef<PlayerType | null>(null)
  const containerRef = useRef(null)
  const hasTrackedPlay = useRef(false)
  const hasTrackedEnded = useRef(false)

  const loadPlayerScript = useCallback(() => {
    if (!window.Vimeo) {
      var script = document.createElement('script')
      script.src = 'https://player.vimeo.com/api/player.js'
      script.onload = () => setVimeoScriptLoaded(true)
      document.head.appendChild(script)
    }
  }, [])

  // Convert any Vimeo URL to proper embed URL with inline parameters
  const getEmbedUrl = useCallback((url: string): string => {
    if (url.includes('player.vimeo.com')) {
      // Add inline playback parameters to existing embed URL
      const urlObj = new URL(url)
      urlObj.searchParams.set('pip', '0')
      urlObj.searchParams.set('autopause', '0')
      return urlObj.toString()
    }

    // Extract video ID from various Vimeo URL formats
    const getVideoId = (videoUrl: string): string => {
      const match = videoUrl.match(/vimeo\.com\/(?:.*\/)?(\d+)/)
      return match ? match[1] : videoUrl.split('/').pop() || videoUrl
    }
    const videoId = getVideoId(url)

    // Build proper embed URL with parameters for inline playback
    const embedParams = new URLSearchParams({
      autoplay: '0',
      muted: '0',
      controls: '1',
      pip: '0',
      autopause: '0',
      dnt: '1',
      app_id: '122963', // Generic app ID for embedding
    })

    return `https://player.vimeo.com/video/${videoId}?${embedParams.toString()}`
  }, [])

  const createPlayer = useCallback(
    (element): PlayerType => {
      const player = new window.Vimeo.Player(element)
      player.ready().then(function () {
        // The iframe player is ready
        setIsIframeReady(true)
      })

      if (props.trackingSource) {
        const eventId = props.eventId || 'vimeo_video_player_interacted'
        player.on('play', function () {
          if (hasTrackedPlay.current) return
          hasTrackedPlay.current = true
          logAmplitudeEvent(eventId, {
            url: props.videoUrl,
            action: 'played',
            source: props.trackingSource,
            notif_id: props.notifId,
          })
        })

        player.on('ended', function () {
          if (hasTrackedEnded.current) return
          hasTrackedEnded.current = true
          logAmplitudeEvent(eventId, {
            url: props.videoUrl,
            action: 'finished',
            source: props.trackingSource,
            notif_id: props.notifId,
          })
        })
      }
      return player
    },
    [props.videoUrl, props.trackingSource, props.eventId, props.notifId]
  )

  useEffect(() => {
    hasTrackedPlay.current = false
    hasTrackedEnded.current = false
  }, [props.videoUrl])

  const destroyPlayer = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.destroy()
      playerRef.current = null
    }
  }, [])

  useEffect(() => {
    loadPlayerScript()
    return destroyPlayer
  }, [])

  useEffect(() => {
    if (containerRef.current && vimeoScriptLoaded && !playerRef.current && props.videoUrl) {
      const player = createPlayer(containerRef.current)
      playerRef.current = player
    }
  }, [vimeoScriptLoaded, containerRef.current, props.videoUrl, createPlayer])

  const embedUrl = getEmbedUrl(props.videoUrl)

  return (
    <div className={classes.container}>
      {(!isIframeReady || !props.videoUrl) && <LoadingDots />}
      {vimeoScriptLoaded && (
        <iframe
          ref={containerRef}
          src={embedUrl}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          frameBorder={0}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  )
}
export default ResponsiveVimeoPlayer
